import {
  RoutePlanParams,
  paramsSummary,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-plan-params';

/**
 * El **recorrido de decisión** de una parada: qué quedó decidido en cada tramo y
 * por qué camino se llegó ahí.
 *
 * Por qué existe: la fila de una parada dice cliente, ruta y territorio, y ninguno
 * de los tres responde la pregunta que se hace antes de aceptar o mover algo —cómo
 * terminó este cliente en este camión—. Los tramos ya estaban resueltos y guardados
 * (`TerritoryAssignment` con su método y sus km, `ResolvedTerritoryFleet` con su
 * tamaño y la procedencia de su capacidad); lo que faltaba era poder leerlos por
 * pedido en vez de aplastados en un `title` de diecisiete líneas.
 *
 * **Se enuncia lo decidido, no quién lo decidió.** El contrato del motor devuelve
 * rutas y no asignadas con motivo, y NO informa razones. Un tramo redactado como
 * "el motor eligió este camión porque…" sería una reconstrucción nuestra con la
 * autoridad de una traza: se ve igual de confiable que el resto de la pantalla y no
 * lo es. Cuando un tramo tuvo una sola opción, decirlo cierra la pregunta; cuando
 * tuvo varias, se declara cuántas eran y se detiene ahí.
 *
 * Módulo puro: entra lo que se sabe de la parada, salen los tramos y la marca. Sin
 * HTTP, sin Angular.
 */

/** Cómo quedó determinado el territorio del pedido. */
export type TerritoryBasis =
  /** Su ruta de entrega lo declara: el dato estaba, no hubo escalera. */
  | 'declared'
  /** Es el único territorio suyo con flota. */
  | 'single-fleet'
  /** Varios lo tenían y se resolvió por cercanía. */
  | 'nearest';

/** Cómo quedó ubicada la parada en la ruta que la muestra. */
export type Placement =
  /** La produjo la corrida y sigue donde quedó. */
  | 'run'
  /** El operador la trajo de otra ruta. */
  | 'moved'
  /** El operador la incorporó desde los no asignados. */
  | 'injected';

/**
 * El distintivo de la fila. **Se deriva del recorrido**, no se pasa aparte: un
 * campo suelto se desincronizaría con el primer caso nuevo.
 */
export type DecisionMark =
  | 'declared'
  | 'inferred'
  | 'among'
  | 'manual'
  | 'mismatch';

/**
 * Orden de urgencia, de más a menos. `manual` gana a todo porque cambia QUÉ
 * SIGNIFICA el resto del recorrido: los tramos describen la corrida, y la corrida
 * ya no explica dónde está la parada. El disconforme sigue enunciado en los tramos.
 */
const MARK_PRIORITY: readonly DecisionMark[] = [
  'manual',
  'mismatch',
  'inferred',
  'among',
  'declared',
];

/** Rótulo corto de cada marca, para el `title` de la fila. */
export const DECISION_MARK_LABELS: Readonly<Record<DecisionMark, string>> = {
  declared: 'El territorio viene de su ruta',
  inferred: 'El territorio se dedujo: el pedido no lo trae',
  among: 'El territorio tenía más de un camión',
  manual: 'Esta parada la pusiste vos, no el optimizador',
  mismatch: 'Este territorio no es de los que atiende su ruta',
};

/**
 * Ícono de cada marca. Todos salen del juego que la pantalla ya usa: uno que el
 * registro no tenga se pinta como un hueco y el aviso queda en la consola.
 */
export const DECISION_MARK_ICONS: Readonly<Record<DecisionMark, string>> = {
  declared: 'heroicons_outline:check-circle',
  inferred: 'heroicons_outline:viewfinder-circle',
  among: 'heroicons_outline:truck',
  manual: 'heroicons_outline:map-pin',
  mismatch: 'heroicons_outline:exclamation-triangle',
};

/**
 * Clase de color de cada marca.
 *
 * `declared` va casi invisible a propósito: en una ruta de catorce paradas, doce
 * son el caso normal, y una marca visible en todas no señala nada. Está —cada fila
 * lleva la suya, y el `title` la nombra— pero no compite.
 */
export const DECISION_MARK_CLASSES: Readonly<Record<DecisionMark, string>> = {
  declared: 'text-slate-300',
  inferred: 'text-amber-500',
  among: 'text-indigo-500',
  manual: 'text-violet-500',
  mismatch: 'text-amber-600',
};

/**
 * Clase del **chip** de cada marca: fondo y texto.
 *
 * Existe aparte de `DECISION_MARK_CLASSES` —que es solo el color del icono— porque el
 * control paso de ser un icono suelto a un chip rotulado, y un chip necesita fondo. Se
 * conserva la discriminacion: `declared` queda en el gris mas apagado, asi que en una
 * ruta donde doce de catorce paradas son el caso normal la fila no se llena de color.
 */
export const DECISION_MARK_CHIP_CLASSES: Readonly<Record<DecisionMark, string>> = {
  declared: 'bg-slate-100 text-slate-500 hover:bg-slate-200',
  inferred: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
  among: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100',
  manual: 'bg-violet-50 text-violet-700 hover:bg-violet-100',
  mismatch: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
};

/** Un tramo del recorrido. */
export interface DecisionStep {
  /** Qué quedó decidido. */
  what: string;
  /** Por qué camino se llegó a eso. */
  how: string;
  /** `true` si no hubo alternativa: no hay elección que explicar. */
  sole?: boolean;
}

/** Qué cambia por tener la parada donde el operador la puso. */
export interface MoveConsequence {
  /** Km en línea recta a la parada más cercana de la ruta. */
  detourKm?: number;
  /** Carga vigente del camión, en libras. */
  loadLb: number;
  /** Capacidad del camión por viaje, en libras. */
  capacityLb: number;
  /** Minutos por encima de la jornada, cuando se excede. */
  overShiftMin?: number;
}

export interface StopDecisionInput {
  /** Territorio de la parada, tal como se rotula. */
  territoryName?: string;
  /** Ruta que la sirve. */
  routeName?: string;
  /** Camión de la ruta. */
  vehicleLabel?: string;
  /** Cómo quedó determinado el territorio. Ausente = no se supo. */
  basis?: TerritoryBasis;
  /** Km que decidieron el empate, cuando `basis` es `nearest`. */
  distanceKm?: number;
  /** Camiones que sirven el territorio. Ausente = no se supo. */
  fleetSize?: number;
  /**
   * Los parámetros con los que se planificó la ruta.
   *
   * Reemplazan a una bandera `capacityAssumed` suelta: la capacidad es UNO de los
   * insumos, y tenerlos todos permite que el tramo de ubicación diga con qué se
   * ubicó la parada en vez de decir que "la ubicó la corrida", que no afirmaba nada.
   */
  params?: RoutePlanParams;
  /** El territorio no está entre los que declara la ruta. */
  territoryMismatch?: boolean;
  /** Cómo quedó ubicada la parada. */
  placement: Placement;
  /** Consecuencia medida. Solo se enuncia si la ubicación es manual. */
  consequence?: MoveConsequence;
}

export interface StopDecision {
  steps: DecisionStep[];
  mark: DecisionMark;
  markLabel: string;
  markIcon: string;
  markClass: string;
  /** Fondo y texto del chip que abre el recorrido. */
  markChipClass: string;
}

/** Miles con coma, el mismo formato que la barra de capacidad de la tarjeta. */
function lb(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} lb`;
}

/** Un decimal, y sin cola cuando es entero — igual que `number: '1.0-1'`. */
function km(value: number): string {
  return `${Math.round(value * 10) / 10} km`;
}

/** El tramo del territorio, o nada cuando no se sabe cuál es. */
function territoryStep(input: StopDecisionInput): DecisionStep | undefined {
  const { territoryName, routeName, basis, distanceKm } = input;
  if (!territoryName) return undefined;

  const what = `Territorio ${territoryName}`;

  if (basis === 'nearest') {
    // Sin distancia medible no se afirma una distancia: la escalera puede resolver
    // por territorio compartido y quedarse sin geometría con la cual medir.
    const detalle = distanceKm === undefined ? '' : ` (${km(distanceKm)})`;
    return {
      what,
      how: `el cliente tiene varios territorios con camión; se eligió el más cercano${detalle}`,
    };
  }
  if (basis === 'single-fleet') {
    return { what, how: 'es el único territorio del cliente con camión' };
  }
  // `declared` y el caso sin base conocida se dicen igual: lo trae el pedido. Sin
  // nombre de ruta se omite el nombre, no se escribe un hueco.
  return {
    what,
    how: routeName ? `viene de su ruta ${routeName}` : 'viene de la ruta del pedido',
  };
}

/** El tramo de la flota: cuántos camiones había y cuál quedó. */
function fleetStep(input: StopDecisionInput): DecisionStep | undefined {
  const { fleetSize, vehicleLabel } = input;
  if (!vehicleLabel && fleetSize === undefined) return undefined;

  // La capacidad NO se menciona acá: es un parámetro, y los parámetros los enumera el
  // tramo de ubicación. Nombrarla en dos tramos daría dos lugares que corregir.
  const cual = vehicleLabel ?? 'el camión de la ruta';

  if (fleetSize === 1) {
    return { what: 'Camiones del territorio', how: cual, sole: true };
  }
  if (fleetSize !== undefined && fleetSize > 1) {
    // Cuántos había y cuál quedó. Y se detiene: por qué quedó ese no está en
    // ninguna respuesta del optimizador, y escribirlo sería inventarlo.
    return { what: 'Camiones del territorio', how: `${fleetSize}, quedó en ${cual}` };
  }
  // Sin tamaño conocido NO se marca `sole`: afirmaría que no hubo alternativa.
  return { what: 'Camiones del territorio', how: cual };
}

/**
 * El tramo de la ubicación vigente.
 *
 * Para la que ubicó la planificación: **nombra al optimizador y sus insumos**. Antes
 * decía "la ubicó la corrida y sigue donde quedó" —evitaba atribuirle un motivo, que
 * es correcto, pero no afirmaba nada y se leía raro—. Nombrar los parámetros no es
 * reconstruir el motivo: son datos verificables contra la petición que se envió.
 *
 * Para una ubicación MANUAL no se nombra al optimizador ni aunque haya parámetros:
 * la parada no está donde él la puso.
 */
function placementStep(placement: Placement, params?: RoutePlanParams): DecisionStep {
  const what = 'Ubicación en la ruta';
  if (placement === 'injected') {
    return { what, how: 'la agregaste vos desde los que no habían entrado' };
  }
  if (placement === 'moved') {
    return { what, how: 'la moviste vos desde otra ruta' };
  }
  // Sin parámetros conocidos se dice solo quién la ubicó: enumerar huecos no informa.
  const insumos = paramsSummary(params);
  return { what, how: insumos ? `la ubicó el optimizador · ${insumos}` : 'la ubicó el optimizador' };
}

/**
 * El tramo de la consecuencia, solo para una ubicación manual.
 *
 * Enuncia lo que cambia; NO recomienda. La compuerta de territorio ya decide lo que
 * no se puede hacer, y para lo que sí se puede el operador tiene contexto que la
 * pantalla no tiene —un cliente que cierra temprano, un chofer que conoce el
 * barrio—. Un consejo lo sustituiría; una consecuencia medida lo informa.
 */
function consequenceStep(input: StopDecisionInput): DecisionStep | undefined {
  if (input.placement === 'run') return undefined;
  const c = input.consequence;
  if (!c) return undefined;

  const partes: string[] = [];
  if (c.detourKm !== undefined) {
    partes.push(`a ${km(c.detourKm)} de la parada más cercana de esta ruta`);
  }
  partes.push(`la ruta lleva ${lb(c.loadLb)} de ${lb(c.capacityLb)}`);
  if (c.overShiftMin !== undefined && c.overShiftMin > 0) {
    partes.push(`se pasa ${c.overShiftMin} min de la jornada`);
  }

  return { what: 'Qué cambia por tenerla acá', how: partes.join(' · ') };
}

/** La marca más urgente de las que aplican. */
function markOf(input: StopDecisionInput): DecisionMark {
  const aplica = new Set<DecisionMark>(['declared']);
  if (input.placement !== 'run') aplica.add('manual');
  if (input.territoryMismatch) aplica.add('mismatch');
  if (input.basis === 'nearest' || input.basis === 'single-fleet') aplica.add('inferred');
  if (input.fleetSize !== undefined && input.fleetSize > 1) aplica.add('among');

  return MARK_PRIORITY.find((m) => aplica.has(m));
}

/**
 * El recorrido de decisión de una parada.
 *
 * Los tramos salen en el orden en que la decisión se fue cerrando: primero dónde
 * cae el pedido, después con qué se lleva, después dónde quedó en la ruta, y al
 * final —solo si la ubicación es manual— qué cambia por tenerla ahí.
 */
export function stopDecision(input: StopDecisionInput): StopDecision {
  const steps = [
    territoryStep(input),
    fleetStep(input),
    placementStep(input.placement, input.params),
    consequenceStep(input),
  ].filter((s): s is DecisionStep => !!s);

  const mark = markOf(input);
  return {
    steps,
    mark,
    markLabel: DECISION_MARK_LABELS[mark],
    markIcon: DECISION_MARK_ICONS[mark],
    markClass: DECISION_MARK_CLASSES[mark],
    markChipClass: DECISION_MARK_CHIP_CLASSES[mark],
  };
}
